import React, { useState, useEffect, useRef } from 'react'
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import VPOProutineProgram from './VPOProutineProgram';
import VPOPCampaignProgram from './VPOPCampaignProgram';
import utils from '../../utils';
import useDbApiKey from "../../useDbApiKey";
import PillsDynamic from '../Layout/PillsDynamic';
import ToastProvider, { useToast } from "../ToastProvider";
//import { type } from '@testing-library/user-event/dist/cjs/utility/type.js';

function VPOPvaccineProgram({ 
  vaccineProgram, 
  lastItem=null, 
  selectedISO=null, 
  sendDataInputsToParent, 
  productOptions, 
  productDictionary, 
  campaignTypeOptions,
  onApiCallStart,
  onApiCallComplete 
}) {

  const { addToast } = useToast();
  const { apiKey } = useDbApiKey();
  const [showProgram, setShowProgram] = useState(false);
  const [baseLineDataCampaign, setBaseLineDataCampaign] = useState(null);
  const [optimizedDataCampaign, setOptimizedDataCampaign] = useState(null);
  const [programDataRoutine, setProgramDataRoutine] = useState(null);
  const [optimizedDataRoutine, setOptimizedDataRoutine] = useState([]);

  // Keep a ref to productDictionary so we can use it in the routine useEffect
  // without it being a dependency (which causes double-firing on load)
  const productDictionaryRef = useRef(productDictionary);
  useEffect(() => {
    productDictionaryRef.current = productDictionary;
  }, [productDictionary]);

  const currentYear = new Date().getFullYear();
  const yearsToShow = [currentYear, currentYear + 1, currentYear + 2, currentYear + 3, currentYear + 4];
  const scheduleOptions = ["1-dose","2-dose","3-dose","4-dose","3+0","2+1"];




// Fetches baseline campaign program data for the selected country and vaccine group.
// If no program exists, reports an empty baseline to the parent to keep the baseline counter in sync.
// Retriggers when a new country is selected.
useEffect(() => {
    if (!apiKey || !selectedISO?.geography_id) return;

    (async () => {
        try {
          let result = await utils.getData("get_campaign_program", apiKey, "?geography_id=" + selectedISO.geography_id + "&vaccine_group=" + vaccineProgram.vaccineGroup);
          if (result && result.campaign_years && result.campaign_years.length > 0) {
            setBaseLineDataCampaign(result);
            // Deep copy to avoid shared references
            const deepCopy = JSON.parse(JSON.stringify(result));
            setOptimizedDataCampaign(deepCopy);
            fetchOptimizedCampaign(deepCopy, "baseline");
          } else {
            sendDataInputsToParent([], vaccineProgram.vaccineGroup, "no_program");
          }
        } catch (error) {
          console.error("Error resetting program view:", error);
          addToast("Error resetting program view: " + error.message, { appearance: "error" });
        }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedISO, apiKey]);


// Fetches baseline ROUTINE program data for the selected country and vaccine group.
// If no program exists, reports no_program to the parent to keep the baseline counter in sync.
// Uses a ref for productDictionary to avoid double-firing when the dictionary loads.
// Retriggers only when a new country is selected.
useEffect(() => {
    if (!apiKey || !selectedISO?.geography_id) return;
    if (Object.keys(productDictionaryRef.current).length === 0) return;

    (async () => {
      try {
        const routineProgram = await utils.getData("get_routine_program", apiKey, "?geography_id=" + selectedISO.geography_id + "&vaccine_group=" + vaccineProgram.vaccineGroup);
        
        if (routineProgram) {
          const programs = Array.isArray(routineProgram) ? routineProgram : [routineProgram];
          const validPrograms = programs.filter(rp => rp.geography_id);
          
          if (validPrograms.length === 0) {
            sendDataInputsToParent([], vaccineProgram.vaccineGroup, "no_program");
            return;
          }

          setProgramDataRoutine(validPrograms);
          const baselineRoutineDataObjs = validPrograms.map(rp => getBaselineRoutineDataObj(rp));
          setOptimizedDataRoutine(baselineRoutineDataObjs);

          if (validPrograms.length === 1) {
            await fetchOptimizedRoutine(baselineRoutineDataObjs[0], "baseline");
          }
        } else {
          sendDataInputsToParent([], vaccineProgram.vaccineGroup, "no_program");
        }
      } catch (error) {
        console.error("Error resetting program view:", error);
        addToast("Error resetting program view: " + error.message, { appearance: "error" });
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedISO, apiKey]);



  // Formats the baseline routine program data into the structure needed for both displaying the current routine program and for 
  // sending as the baseline scenario when fetching optimized routine program results
const getBaselineRoutineDataObj = (routineProgram) => {
    return {
      "program_id": routineProgram.program_id ?? null,
      "geography_id": routineProgram.geography_id,
      "intro_switch_month": routineProgram.intro_switch_month ?? null,
      "intro_switch_year": routineProgram.intro_switch_year ?? null,
      // The program carries its own current product. Looking it up in
      // productDictionary only found it when it happened to also be a switch
      // option, which is why products like Nigeria's MenAfriVac priced as null.
      "current_product_characteristic": routineProgram.current_product_characteristic
        ?? productDictionary[routineProgram.current_product_characteristic_id]
        ?? null,
      "product_characteristic": productDictionary[routineProgram.switch_product_characteristic_id] ?? null,
      "schedule": routineProgram.schedule ?? null,
      "multiplier": routineProgram.multiplier ?? null,
    }
}
 



  //Adds a new campaign row to the optimized campaign program data which will trigger the rendering of a new set of dropdowns for the user to input the details of the new campaign program scenario they want to test
  const addCampaignRow = () => {
    // Use deep copy for consistency, and initialize if optimizedDataCampaign is null
    let copy;
    if (optimizedDataCampaign) {
      copy = JSON.parse(JSON.stringify(optimizedDataCampaign));
    } else {
      copy = {
        geography_id: selectedISO?.geography_id ?? null,
        campaign_years: []
      };
    }
    let newCampaignRow = {
      year: null,
      campaign_type: null,  
      min_age: null,
      max_age: null,
      product_characteristic: null,
      product_characteristic_id: null
    };

    if (!copy.campaign_years) {
      copy.campaign_years = [];
    }
    copy.campaign_years.push(newCampaignRow);
    setOptimizedDataCampaign(copy);
  }


const removeCampaignRow = async (index) => {
    // Use deep copy to avoid shared array references
    let copy = JSON.parse(JSON.stringify(optimizedDataCampaign));  
    if (copy.campaign_years && copy.campaign_years.length > index) {
      copy.campaign_years.splice(index, 1);
      setOptimizedDataCampaign(copy);
      
      if (copy.campaign_years.length === 0) {
        // Send empty array to clear optimized data when no campaigns remain - kind of weird, but need a way to determine campaign vs routine
        sendDataInputsToParent([{ type: 'campaign' }], vaccineProgram.vaccineGroup, "optimized");
      } else {
        await fetchOptimizedCampaign(copy, "optimized");
      }
    } 
}

const handleRoutineDelete = async (index) => {
    const copy = JSON.parse(JSON.stringify(optimizedDataRoutine));
    copy.splice(index, 1);
    setOptimizedDataRoutine(copy);
    
    if (copy.length === 0) {
        // kind of weird, but need a way to determine campaign vs routine
        sendDataInputsToParent([{ type: 'routine' }], vaccineProgram.vaccineGroup, "optimized");
    } else if (copy.length === 1) {
        await fetchOptimizedRoutine(copy[0], "optimized");
    }
}
  
  // Handles the optimization of the routine program by updating the optimized routine program data with the user's selections for the new routine program scenario
  // then triggering the fetch for optimized routine program results based on the updated optimized routine program data
const handleRoutineOptimization = async (index, e) => {
    let copy = [...optimizedDataRoutine];
    let program = { ...copy[index] };
    
    if (e.product_characteristic) {
      program.product_characteristic = productDictionary[e.product_characteristic];
    }
    if (e.schedule) {
      program.schedule = e.schedule;
    }
    if (e.start_year) {
      program.intro_switch_year = e.start_year;
    }
    
    copy[index] = program;
    setOptimizedDataRoutine(copy);

    if (copy.length === 1) {
        await fetchOptimizedRoutine(copy[0], "optimized");
    }
}

const handleCampaignOptimization = async (index, e, fullCampaign) => {
  let copy = JSON.parse(JSON.stringify(optimizedDataCampaign));

  if (!copy.campaign_years || copy.campaign_years.length <= index) return;

  let row = copy.campaign_years[index];

  // REQUIRED top-level fields
  copy.geography_id = selectedISO?.geography_id;
  copy.gender = fullCampaign?.gender || "Both";

  switch (e.type) {
    case 'year':
      row.year = Number(e.value);
      break;

    case 'campaign_type':
      row.campaign_type = e.value;
      break;

    case 'min_age':
      row.min_age = Number(e.value);
      break;

    case 'max_age':
      row.max_age = Number(e.value);
      break;

    case 'uptake_or_trs':
      row.uptake_or_trs = Number(e.value);
      break;

    case 'product_characteristic':
      const pc = productDictionary[e.value];
      row.product_characteristic = pc;
      row.product_characteristic_id = pc?.product_characteristic_id;
      break;

    default:
      break;
  }

  // REQUIRED per-row fields
  row.dose_sequence = row.dose_sequence ?? 1;
  row.multiplier = row.multiplier ?? 1;
  setOptimizedDataCampaign(copy);
  await fetchOptimizedCampaign(copy, "optimized");
};
  
const fetchOptimizedRoutine = async (routineData, scenario) => {
        // Notify parent that API call is starting
    if (onApiCallStart) {
        onApiCallStart(vaccineProgram.vaccineGroup, 'routine');
    }
    
    try {
        let routineProgram = await utils.db_api_post("compute_routine_analysis", apiKey, routineData);
        if (routineProgram && Array.isArray(routineProgram)) {
            let formattedResponse = routineProgram.map((item, index) => ({
                row_id: `${vaccineProgram.vaccineGroup}-routine-${scenario}-${item.year}-${index}`,
                vaccine: item.antigen ?? null,
                type: 'routine',
                year: item.year,
                scenario: scenario,
                trs: item.total_required_supply ?? null,
                cost_to_country: item.cost_to_country ?? null,
                cost_to_gavi: item.cost_to_gavi ?? null,
                cvb_category: item.cvb_category ?? null,
                guaranteed_budget: item.guaranteed_budget ?? null,
                discretionary_budget: item.discretionary_budget ?? null,
            }));
            sendDataInputsToParent(formattedResponse, vaccineProgram.vaccineGroup, scenario);
        }
    } catch (error) {
        console.error(`[ROUTINE ERROR] ${vaccineProgram.vaccineGroup}:`, error);
    } finally {
        // Notify parent that API call is complete
        if (onApiCallComplete) {
            onApiCallComplete(vaccineProgram.vaccineGroup, 'routine');
        }
    }
}

const fetchOptimizedCampaign = async (campaignData, scenario) => {
    // Notify parent that API call is starting
    if (onApiCallStart) {
        onApiCallStart(vaccineProgram.vaccineGroup, 'campaign');
    }

    try {
        let campaignProgram = await utils.db_api_post("compute_campaign_analysis", apiKey, campaignData);
        if (campaignProgram && Array.isArray(campaignProgram)) {
            let formattedResponse = campaignProgram.map((item, index) => ({
                row_id: `${vaccineProgram.vaccineGroup}-campaign-${scenario}-${item.year}-${index}`,
                vaccine: item.antigen ?? null,
                type: 'campaign',
                year: item.year,
                scenario: scenario,
                trs: item.total_required_supply ?? null,
                cost_to_country: item.cost_to_country ?? null,
                cost_to_gavi: item.cost_to_gavi ?? null,
                cvb_category: item.cvb_category ?? null,
                guaranteed_budget: item.guaranteed_budget ?? null,
                discretionary_budget: item.discretionary_budget ?? null,
            }));
            sendDataInputsToParent(formattedResponse, vaccineProgram.vaccineGroup, scenario);
        }
    } catch (error) {
        console.error(`[CAMPAIGN ERROR] ${vaccineProgram.vaccineGroup}:`, error);
    } finally {
        // Notify parent that API call is complete
        if (onApiCallComplete) {
            onApiCallComplete(vaccineProgram.vaccineGroup, 'campaign');
        }
    }
}

  return (
    <ToastProvider>
      <div className={"py-03 px-04 border rounded" + (lastItem ? "" : " mb-03")}>
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setShowProgram(true)}
        >
          <div className='flex items-center gap-04'>
            <span className="body-medium-comfortable text-[var(--text-secondary-light)]">{vaccineProgram.vaccineProgram}</span>
            {
              (baseLineDataCampaign || programDataRoutine) ? 
                <PillsDynamic 
                  pillList={['Baseline']}
                  className='basic-primary-pill border-corner-radius-rounding-08 label-small'
                  handleEdit={null}
                  customIcon={false} // Allow custom icon, default to `X`
                  emptyState={null} // Custom empty state
                />
              : null
            }
          </div>
          {
            showProgram ? (
              <ChevronUp
                className="inline-block"
                onClick={e => {
                  e.stopPropagation();
                  setShowProgram(false);
                }}
              />
            ) 
            : 
            (
              <ChevronDown className="inline-block" />
            )
          }
        </div>
        {
          showProgram ?
            <div className='pb-04 px-03'>
              <VPOProutineProgram
                yearsToShow={yearsToShow} 
                baselineData={programDataRoutine} 
                optimizedData={optimizedDataRoutine} 
                handleRoutineDelete={handleRoutineDelete}
                handleRoutineOptimization={handleRoutineOptimization} 
                productOptions={productOptions} 
                scheduleOptions={scheduleOptions}
                optimizedDataRoutine={optimizedDataRoutine}
              />
              <VPOPCampaignProgram 
                productOptions={productOptions}
                yearsToShow={yearsToShow} 
                baselineData={baseLineDataCampaign} 
                optimizedData={optimizedDataCampaign} 
                addCampaignRow={addCampaignRow} 
                removeCampaignRow={removeCampaignRow}
                campaignTypeOptions={campaignTypeOptions}
                handleCampaignOptimization={handleCampaignOptimization}
              />
            </div>
          : null
        }
      </div>
    </ToastProvider>
  )
}

export default VPOPvaccineProgram;